import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  Brain, Gauge, ShoppingBag, ChevronRight,
  RotateCcw, Radio, Upload, PenLine,
  CheckCircle2, Loader2,
} from "lucide-react";
import { toast } from "sonner";

//  Types 

interface Props {
  userId: string;
  onComplete: () => void;
}

interface FormData {
  // Step 2
  experience_level: string;
  primary_instruments: string[];
  preferred_sessions: string[];
  trades_per_day: string;
  uses_prop_firm: boolean;
  prop_firm_name: string;
  account_size: string;
  // Step 3
  max_risk_per_trade_pct: number;
  daily_loss_limit: string;
  max_trades_per_day: number;
  trading_hours_from: string;
  trading_hours_to: string;
  // Step 4 quick trade
  quick_instrument: string;
  quick_direction: "LONG" | "SHORT";
  quick_pnl: string;
  quick_date: string;
}

//  Constants 

const TOTAL_STEPS = 6;

const EXPERIENCE_LEVELS = ["BEGINNER", "INTERMEDIATE", "ADVANCED", "PROFESSIONAL"];
const INSTRUMENTS = ["FOREX", "FUTURES", "CFD", "CRYPTO", "STOCKS", "OPTIONS"];
const SESSIONS = ["LONDON", "NEW YORK", "ASIA", "ALL SESSIONS"];
const TRADES_PER_DAY = ["1-2", "3-5", "6-10", "10+"];
const ACCOUNT_SIZES = ["Under $10k", "$10k—$25k", "$25k—$50k", "$50k—$100k", "$100k—$200k", "Over $200k"];

const AGENT_STARTUP = [
  { key: "trainer", label: "Trainer Agent", msg: "Building your profile…" },
  { key: "coach",   label: "Coach Agent",   msg: "Preparing first coaching…" },
  { key: "calendar",label: "Calendar Agent",msg: "Checking today's events…" },
  { key: "pattern", label: "Pattern Agent", msg: "Ready (needs more trades)" },
  { key: "news",    label: "News Agent",    msg: "Fetching market intelligence…" },
];

//  Small primitives 

function Chip({
  label, selected, onClick, multi,
}: {
  label: string; selected: boolean; onClick: () => void; multi?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-8 px-4 text-[11px] font-medium uppercase tracking-[0.08em] border transition-colors duration-100",
        selected
          ? "border-[#1d9e75] text-[#1d9e75] bg-[#1d9e75]/[0.08]"
          : "border-white/[0.08] text-white/35 hover:text-white/60 hover:border-white/20"
      )}
    >
      {label}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-white/35 mb-2">
      {children}
    </p>
  );
}

//  Step renders 

function Step1() {
  const features = [
    { icon: Brain, title: "AI NETWORK", body: "7 agents working for you 24/7" },
    { icon: Gauge, title: "EDGE SCORE", body: "Track your discipline 300—850" },
    { icon: ShoppingBag, title: "MARKETPLACE", body: "Copy verified strategies from pros" },
  ];
  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold tracking-[0.04em] uppercase">
          Welcome to Margin Metrics
        </h1>
        <p className="text-sm text-white/40">
          Your professional trading intelligence platform
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {features.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="p-4 border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01] space-y-3"
          >
            <Icon size={20} strokeWidth={1.5} className="text-[#1d9e75]" />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-white mb-1">{title}</p>
              <p className="text-[11px] text-white/35 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step2({ data, set }: { data: FormData; set: (k: keyof FormData, v: any) => void }) {
  const toggleArr = (field: "primary_instruments" | "preferred_sessions", val: string) => {
    const arr = data[field] as string[];
    set(field, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-bold uppercase tracking-[0.06em]">Tell Us About Your Trading</h2>
        <p className="text-[12px] text-white/35">This helps our AI personalise everything for you</p>
      </div>

      <div>
        <SectionLabel>Experience Level</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {EXPERIENCE_LEVELS.map((l) => (
            <Chip key={l} label={l} selected={data.experience_level === l} onClick={() => set("experience_level", l)} />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Primary Instruments</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {INSTRUMENTS.map((i) => (
            <Chip key={i} label={i} selected={data.primary_instruments.includes(i)} onClick={() => toggleArr("primary_instruments", i)} multi />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Preferred Sessions</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {SESSIONS.map((s) => (
            <Chip key={s} label={s} selected={data.preferred_sessions.includes(s)} onClick={() => toggleArr("preferred_sessions", s)} multi />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Typical Trades Per Day</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {TRADES_PER_DAY.map((t) => (
            <Chip key={t} label={t} selected={data.trades_per_day === t} onClick={() => set("trades_per_day", t)} />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Prop Firm Account?</SectionLabel>
        <div className="flex gap-2 mb-3">
          <Chip label="YES" selected={data.uses_prop_firm} onClick={() => set("uses_prop_firm", true)} />
          <Chip label="NO" selected={!data.uses_prop_firm} onClick={() => set("uses_prop_firm", false)} />
        </div>
        {data.uses_prop_firm && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <SectionLabel>Which Firm?</SectionLabel>
              <input
                type="text"
                placeholder="e.g. FTMO, MyForexFunds…"
                value={data.prop_firm_name}
                onChange={(e) => set("prop_firm_name", e.target.value)}
                className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm text-white placeholder:text-white/20 outline-none focus:border-white/20 font-mono"
              />
            </div>
            <div>
              <SectionLabel>Account Size</SectionLabel>
              <select
                value={data.account_size}
                onChange={(e) => set("account_size", e.target.value)}
                className="w-full h-9 bg-black border border-white/[0.08] px-3 text-sm text-white outline-none focus:border-white/20 appearance-none"
              >
                <option value="">Select…</option>
                {ACCOUNT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Step3({ data, set }: { data: FormData; set: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-bold uppercase tracking-[0.06em]">Set Your Risk Parameters</h2>
        <p className="text-[12px] text-white/35">Our AI uses these to protect your account</p>
      </div>

      {/* Max risk slider */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Max Risk Per Trade</SectionLabel>
          <span className="text-sm font-mono text-white">{data.max_risk_per_trade_pct.toFixed(1)}%</span>
        </div>
        <input
          type="range"
          min={0.5} max={5} step={0.5}
          value={data.max_risk_per_trade_pct}
          onChange={(e) => set("max_risk_per_trade_pct", parseFloat(e.target.value))}
          className="w-full accent-[#1d9e75] h-1"
        />
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-white/20 font-mono">0.5%</span>
          <span className="text-[10px] text-white/20 font-mono">5%</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <SectionLabel>Daily Loss Limit ($)</SectionLabel>
          <input
            type="number"
            placeholder="e.g. 500"
            value={data.daily_loss_limit}
            onChange={(e) => set("daily_loss_limit", e.target.value)}
            className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white placeholder:text-white/20 outline-none focus:border-white/20"
          />
        </div>
        <div>
          <SectionLabel>Max Trades Per Day</SectionLabel>
          <input
            type="number"
            min={1} max={50}
            value={data.max_trades_per_day}
            onChange={(e) => set("max_trades_per_day", parseInt(e.target.value) || 1)}
            className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white placeholder:text-white/20 outline-none focus:border-white/20"
          />
        </div>
      </div>

      <div>
        <SectionLabel>Trading Hours (UTC)</SectionLabel>
        <div className="flex items-center gap-3">
          <input
            type="time"
            value={data.trading_hours_from}
            onChange={(e) => set("trading_hours_from", e.target.value)}
            className="h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white outline-none focus:border-white/20 [color-scheme:dark]"
          />
          <span className="text-white/20 text-sm">to</span>
          <input
            type="time"
            value={data.trading_hours_to}
            onChange={(e) => set("trading_hours_to", e.target.value)}
            className="h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white outline-none focus:border-white/20 [color-scheme:dark]"
          />
        </div>
        <p className="text-[10px] text-white/20 mt-1.5">Leave blank to trade any hours</p>
      </div>
    </div>
  );
}

function Step4({
  data, set, userId, onDone,
}: {
  data: FormData;
  set: (k: keyof FormData, v: any) => void;
  userId: string;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<"choose" | "manual" | "csv">("choose");
  const [saving, setSaving] = useState(false);

  const saveManual = async () => {
    if (!data.quick_instrument || !data.quick_pnl) {
      toast.error("Instrument and P&L are required");
      return;
    }
    setSaving(true);
    const pnl = parseFloat(data.quick_pnl);
    const { error } = await supabase.from("trades").insert({
      user_id: userId,
      instrument: data.quick_instrument.toUpperCase(),
      direction: data.quick_direction,
      pnl,
      date: data.quick_date || new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
    });
    setSaving(false);
    if (error) { toast.error("Failed to save trade"); return; }
    toast.success("Trade logged");
    onDone();
  };

  if (mode === "manual") {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h2 className="text-lg font-bold uppercase tracking-[0.06em]">Log Your First Trade</h2>
          <p className="text-[12px] text-white/35">Just the basics — you can add more detail later</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <SectionLabel>Instrument</SectionLabel>
            <input
              type="text"
              placeholder="e.g. EURUSD, NQ, BTC"
              value={data.quick_instrument}
              onChange={(e) => set("quick_instrument", e.target.value)}
              className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white placeholder:text-white/20 outline-none focus:border-white/20 uppercase"
            />
          </div>
          <div>
            <SectionLabel>Direction</SectionLabel>
            <div className="flex gap-2">
              <Chip label="LONG" selected={data.quick_direction === "LONG"} onClick={() => set("quick_direction", "LONG")} />
              <Chip label="SHORT" selected={data.quick_direction === "SHORT"} onClick={() => set("quick_direction", "SHORT")} />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <SectionLabel>P&L ($)</SectionLabel>
            <input
              type="number"
              placeholder="e.g. 250 or -120"
              value={data.quick_pnl}
              onChange={(e) => set("quick_pnl", e.target.value)}
              className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white placeholder:text-white/20 outline-none focus:border-white/20"
            />
          </div>
          <div>
            <SectionLabel>Date</SectionLabel>
            <input
              type="date"
              value={data.quick_date}
              onChange={(e) => set("quick_date", e.target.value)}
              className="w-full h-9 bg-white/[0.03] border border-white/[0.08] px-3 text-sm font-mono text-white outline-none focus:border-white/20 [color-scheme:dark]"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={saveManual}
            disabled={saving}
            className="h-9 px-6 bg-[#1d9e75] text-black text-[11px] font-bold uppercase tracking-[0.08em] hover:bg-[#1d9e75]/90 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : "SAVE TRADE"}
          </button>
          <button type="button" onClick={() => setMode("choose")} className="h-9 px-4 border border-white/[0.08] text-white/40 text-[11px] uppercase tracking-[0.08em] hover:text-white/60">
            BACK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-bold uppercase tracking-[0.06em]">Add Your First Trade</h2>
        <p className="text-[12px] text-white/35">Or import your history to get started immediately</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setMode("csv")}
          className="p-6 border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all text-left space-y-3"
        >
          <Upload size={20} strokeWidth={1.5} className="text-white/40" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-white mb-1">Import CSV</p>
            <p className="text-[11px] text-white/35 leading-relaxed">Upload your trade history from any broker</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          className="p-6 border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all text-left space-y-3"
        >
          <PenLine size={20} strokeWidth={1.5} className="text-white/40" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-white mb-1">Log Manually</p>
            <p className="text-[11px] text-white/35 leading-relaxed">Add your first trade now</p>
          </div>
        </button>
      </div>
      <button type="button" onClick={onDone} className="text-[11px] text-white/25 hover:text-white/50 transition-colors underline underline-offset-2">
        I'll do this later 
      </button>
    </div>
  );
}

function Step5({ onNext }: { onNext: () => void }) {
  const brokers = [
    { name: "Tradovate",    available: false },
    { name: "NinjaTrader", available: false },
    { name: "MT4 / MT5",   available: false },
    { name: "Manual Import", available: true },
  ];
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-bold uppercase tracking-[0.06em]">Connect Your Broker</h2>
        <p className="text-[12px] text-white/35">Optional — enables live data and AI automation</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {brokers.map(({ name, available }) => (
          <div
            key={name}
            className={cn(
              "p-4 border flex items-center justify-between",
              available
                ? "border-[#1d9e75]/40 bg-[#1d9e75]/[0.04]"
                : "border-white/[0.06] opacity-50"
            )}
          >
            <span className="text-[12px] font-medium text-white">{name}</span>
            {available ? (
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#1d9e75] border border-[#1d9e75]/30 px-2 py-0.5">
                Available
              </span>
            ) : (
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/25 border border-white/[0.08] px-2 py-0.5">
                Soon
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/25">
        You can connect brokers anytime in Settings  Brokers
      </p>
    </div>
  );
}

function Step6({ userId, onComplete }: { userId: string; onComplete: () => void }) {
  const [agentIdx, setAgentIdx] = useState(0);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    // Trigger trainer-agent for this user
    if (!triggered) {
      setTriggered(true);
      supabase.functions.invoke("trainer-agent", { body: { user_id: userId } }).catch(() => {});
    }

    // Animate agent startup list
    if (agentIdx < AGENT_STARTUP.length) {
      const t = setTimeout(() => setAgentIdx((i) => i + 1), 600);
      return () => clearTimeout(t);
    }
  }, [agentIdx, triggered, userId]);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold uppercase tracking-[0.04em]">You're All Set</h2>
        <p className="text-[12px] text-white/35">Your AI agents are warming up</p>
      </div>

      <div className="space-y-2 py-2">
        {AGENT_STARTUP.map(({ key, label, msg }, i) => (
          <div
            key={key}
            className={cn(
              "flex items-center gap-3 p-3 border transition-all duration-500",
              i < agentIdx
                ? "border-[#1d9e75]/20 bg-[#1d9e75]/[0.03]"
                : "border-white/[0.04] opacity-30"
            )}
          >
            <div className={cn(
              "w-1.5 h-1.5 shrink-0 transition-colors duration-300",
              i < agentIdx ? "bg-[#1d9e75]" : "bg-white/20"
            )} />
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-white">{label}</span>
            <span className="text-[11px] text-white/35">{i < agentIdx ? msg : "—"}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onComplete}
        className="w-full h-11 bg-[#1d9e75] text-black text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-[#1d9e75]/90 transition-colors flex items-center justify-center gap-2"
      >
        ENTER MARGIN METRICS <ChevronRight size={16} />
      </button>
    </div>
  );
}

// Compose a starter "trader memory" markdown doc from the onboarding answers.
// This is the first thing the agents read about a new user, and the user can
// edit it anytime in Settings → Memory.
function buildOnboardingMemory(d: FormData): string {
  const lines: string[] = ["# My Trading Profile", ""];
  if (d.experience_level) lines.push(`- **Experience:** ${d.experience_level}`);
  if (d.primary_instruments?.length) lines.push(`- **Instruments I trade:** ${d.primary_instruments.join(", ")}`);
  if (d.preferred_sessions?.length) lines.push(`- **Sessions I trade:** ${d.preferred_sessions.join(", ")}`);
  if (d.trades_per_day) lines.push(`- **Typical trades/day:** ${d.trades_per_day}`);
  if (d.uses_prop_firm) lines.push(`- **Prop firm:** ${d.prop_firm_name || "yes"}${d.account_size ? ` ($${d.account_size} account)` : ""}`);
  const risk: string[] = [];
  if (d.max_risk_per_trade_pct) risk.push(`max ${d.max_risk_per_trade_pct}% risk per trade`);
  if (d.daily_loss_limit) risk.push(`daily loss limit $${d.daily_loss_limit}`);
  if (d.max_trades_per_day) risk.push(`max ${d.max_trades_per_day} trades/day`);
  if (risk.length) lines.push(`- **My risk rules:** ${risk.join(", ")}`);
  lines.push("", "## Notes for my agents", "_Edit this anytime in Settings → Memory. Tell your agents anything you want them to always remember — your strategy, your weaknesses, your goals._");
  return lines.join("\n");
}

//  Main Modal

export function OnboardingModal({ userId, onComplete }: Props) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<FormData>({
    experience_level: "",
    primary_instruments: [],
    preferred_sessions: [],
    trades_per_day: "",
    uses_prop_firm: false,
    prop_firm_name: "",
    account_size: "",
    max_risk_per_trade_pct: 1.0,
    daily_loss_limit: "",
    max_trades_per_day: 5,
    trading_hours_from: "",
    trading_hours_to: "",
    quick_instrument: "",
    quick_direction: "LONG",
    quick_pnl: "",
    quick_date: new Date().toISOString().slice(0, 10),
  });

  const set = (k: keyof FormData, v: any) => setData((d) => ({ ...d, [k]: v }));

  const saveStep2 = async () => {
    // Save to user_profiles
    await supabase.from("user_profiles").upsert({
      user_id: userId,
      onboarding_step: 2,
      experience_level: data.experience_level || null,
      primary_instruments: data.primary_instruments,
      preferred_sessions: data.preferred_sessions,
      trades_per_day: data.trades_per_day || null,
      uses_prop_firm: data.uses_prop_firm,
      prop_firm_name: data.prop_firm_name || null,
      account_size: data.account_size || null,
    }, { onConflict: "user_id" });

    // Pre-seed trader_profiles so trainer-agent has something to start with
    await supabase.from("trader_profiles").upsert({
      user_id: userId,
      experience_level: data.experience_level?.toLowerCase() || null,
      primary_instruments: data.primary_instruments,
      preferred_sessions: data.preferred_sessions,
      uses_prop_firm: data.uses_prop_firm,
      prop_firm_name: data.prop_firm_name || null,
      account_size: data.account_size || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
  };

  const saveStep3 = async () => {
    await supabase.from("risk_settings").upsert({
      user_id: userId,
      max_risk_per_trade_pct: data.max_risk_per_trade_pct,
      daily_loss_limit: data.daily_loss_limit ? parseFloat(data.daily_loss_limit) : null,
      max_trades_per_day: data.max_trades_per_day,
      trading_hours_from: data.trading_hours_from || null,
      trading_hours_to: data.trading_hours_to || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });

    await supabase.from("user_profiles").upsert({
      user_id: userId,
      onboarding_step: 3,
    }, { onConflict: "user_id" });
  };

  const next = async () => {
    setSaving(true);
    try {
      if (step === 2) await saveStep2();
      if (step === 3) await saveStep3();
      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    } catch { /* non-blocking */ }
    setSaving(false);
  };

  const back = () => setStep((s) => Math.max(s - 1, 1));

  const skip = async () => {
    await supabase.from("user_profiles").upsert({
      user_id: userId,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
    onComplete();
  };

  const seedTraderMemory = async () => {
    try {
      // Don't clobber an existing (possibly user-edited) doc
      const { data: existing } = await supabase
        .from("trader_memory" as any)
        .select("content_md")
        .eq("user_id", userId)
        .maybeSingle();
      if ((existing as any)?.content_md?.trim()) return;

      const md = buildOnboardingMemory(data);
      if (!md.trim()) return;
      await supabase.from("trader_memory" as any).upsert(
        { user_id: userId, content_md: md, updated_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    } catch { /* non-blocking — memory seed is best-effort */ }
  };

  const handleComplete = async () => {
    await seedTraderMemory();
    await supabase.from("user_profiles").upsert({
      user_id: userId,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
      onboarding_step: 6,
    }, { onConflict: "user_id" });
    onComplete();
  };

  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;
  const showNext = step < TOTAL_STEPS && step !== 4 && step !== 5;

  return (
    <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-black border border-white/[0.08] flex flex-col max-h-[90vh]">

        {/* Progress bar */}
        <div className="relative h-0.5 bg-white/[0.04] shrink-0">
          <div
            className="absolute left-0 top-0 h-full bg-[#1d9e75] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => i + 1 < step && setStep(i + 1)}
                className={cn(
                  "w-5 h-1 transition-all duration-200",
                  i + 1 === step
                    ? "bg-[#1d9e75]"
                    : i + 1 < step
                    ? "bg-white/30 cursor-pointer hover:bg-white/50"
                    : "bg-white/[0.08]"
                )}
              />
            ))}
          </div>
          <span className="text-[10px] font-mono text-white/25">STEP {step} OF {TOTAL_STEPS}</span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
          {step === 1 && <Step1 />}
          {step === 2 && <Step2 data={data} set={set} />}
          {step === 3 && <Step3 data={data} set={set} />}
          {step === 4 && <Step4 data={data} set={set} userId={userId} onDone={next} />}
          {step === 5 && <Step5 onNext={next} />}
          {step === 6 && <Step6 userId={userId} onComplete={handleComplete} />}
        </div>

        {/* Footer */}
        {step < TOTAL_STEPS && (
          <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between shrink-0">
            {/* Left: back / skip */}
            <div className="flex items-center gap-4">
              {step > 1 && (
                <button
                  type="button"
                  onClick={back}
                  className="text-[11px] text-white/25 hover:text-white/50 transition-colors uppercase tracking-[0.08em]"
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                onClick={skip}
                className="text-[11px] text-white/20 hover:text-white/40 transition-colors underline underline-offset-2"
              >
                Skip setup
              </button>
            </div>

            {/* Right: next */}
            {showNext && (
              <button
                type="button"
                onClick={next}
                disabled={saving}
                className="h-9 px-6 bg-[#1d9e75] text-black text-[11px] font-bold uppercase tracking-[0.08em] hover:bg-[#1d9e75]/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    {step === 1 ? "LET'S GET STARTED" : "CONTINUE"}
                    <ChevronRight size={14} />
                  </>
                )}
              </button>
            )}
            {step === 5 && (
              <button
                type="button"
                onClick={next}
                className="h-9 px-6 bg-[#1d9e75] text-black text-[11px] font-bold uppercase tracking-[0.08em] hover:bg-[#1d9e75]/90 transition-colors flex items-center gap-2"
              >
                SKIP FOR NOW <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
